import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import DownloadIcon from '@mui/icons-material/Download'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { format } from 'date-fns'

interface Report {
  id: string
  name: string
  type: 'end_of_day' | 'weekly' | 'monthly'
  date: Date
  gross_profit: number
  parts_revenue: number
  labor_revenue: number
  status: 'ready' | 'generating' | 'scheduled'
}

const FinancialReports: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(new Date())
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedReport, setSelectedReport] = useState<string | null>(null)

  // Sample reports data
  const reports: Report[] = [
    {
      id: '1',
      name: 'End of Day Report - Sept 19',
      type: 'end_of_day',
      date: new Date('2025-09-19'),
      gross_profit: 52041.74,
      parts_revenue: 59903.63,
      labor_revenue: 77697.82,
      status: 'ready'
    },
    {
      id: '2',
      name: 'Weekly Summary - Sept 15-19',
      type: 'weekly',
      date: new Date('2025-09-19'),
      gross_profit: 52041.74,
      parts_revenue: 59903.63,
      labor_revenue: 77697.82,
      status: 'ready'
    },
    {
      id: '3',
      name: 'Monthly Report - September',
      type: 'monthly',
      date: new Date('2025-09-30'),
      gross_profit: 208166.96,
      parts_revenue: 239614.52,
      labor_revenue: 310791.28,
      status: 'scheduled'
    }
  ]

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, reportId: string) => {
    setAnchorEl(event.currentTarget)
    setSelectedReport(reportId)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedReport(null)
  }

  const generateReport = () => {
    console.log('Generating report for:', startDate, 'to', endDate)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready':
        return 'success'
      case 'generating':
        return 'warning'
      case 'scheduled':
        return 'info'
      default:
        return 'default'
    }
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Financial Reports
      </Typography>

      {/* Report Generator */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Generate Custom Report
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                slotProps={{ textField: { size: 'small' } }}
              />
              <Button
                variant="contained"
                onClick={generateReport}
                disabled={!startDate || !endDate}
              >
                Generate Report
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Reports
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Report Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Gross Profit</TableCell>
                  <TableCell align="right">Parts Revenue</TableCell>
                  <TableCell align="right">Labor Revenue</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>{report.name}</TableCell>
                    <TableCell>{format(report.date, 'MMM d, yyyy')}</TableCell>
                    <TableCell align="right">{formatCurrency(report.gross_profit)}</TableCell>
                    <TableCell align="right">{formatCurrency(report.parts_revenue)}</TableCell>
                    <TableCell align="right">{formatCurrency(report.labor_revenue)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={report.status}
                        color={getStatusColor(report.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {report.status === 'ready' ? (
                        <Box>
                          <IconButton size="small">
                            <DownloadIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, report.id)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>View Details</MenuItem>
        <MenuItem onClick={handleMenuClose}>Download PDF</MenuItem>
        <MenuItem onClick={handleMenuClose}>Download CSV</MenuItem>
        <MenuItem onClick={handleMenuClose}>Email Report</MenuItem>
      </Menu>

      {/* Key Insights */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Key Insights
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body2">
              • Weekly gross profit averaging <strong>$52,041.74</strong> (35% margin)
            </Typography>
            <Typography variant="body2">
              • Labor efficiency improved by <strong>8.2%</strong> compared to last month
            </Typography>
            <Typography variant="body2">
              • Parts revenue showing consistent <strong>55/45 split</strong> between stores
            </Typography>
            <Typography variant="body2">
              • Cash flow positive for <strong>12 consecutive weeks</strong>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default FinancialReports